import { useState } from 'react';
import {
  DocumentChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  CpuChipIcon,
  PhotoIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

interface ModelMetric {
  model: string;
  type: 'text' | 'image' | 'browser';
  tokensUsed?: number;
  imagesGenerated?: number;
  browserActions?: number;
  averageLatency: number;
  cost: number;
  trend: number;
}

const mockModelData: ModelMetric[] = [
  {
    model: 'browser-agent-1',
    type: 'browser',
    browserActions: 15000,
    averageLatency: 350,
    cost: 45.00,
    trend: 18.2,
  },
  {
    model: 'browser-agent-2',
    type: 'browser',
    browserActions: 8500,
    averageLatency: 280,
    cost: 25.50,
    trend: -3.8,
  },
  {
    model: 'gpt-4',
    type: 'text',
    tokensUsed: 1250000,
    averageLatency: 850,
    cost: 125.00,
    trend: 12.5,
  },
  {
    model: 'gpt-3.5-turbo',
    type: 'text',
    tokensUsed: 3500000,
    averageLatency: 250,
    cost: 35.00,
    trend: -5.2,
  },
  {
    model: 'claude-2',
    type: 'text',
    tokensUsed: 2800000,
    averageLatency: 450,
    cost: 84.00,
    trend: 8.3,
  },
  {
    model: 'dall-e-3',
    type: 'image',
    imagesGenerated: 500,
    averageLatency: 2800,
    cost: 75.00,
    trend: 15.8,
  },
  {
    model: 'dall-e-2',
    type: 'image',
    imagesGenerated: 347,
    averageLatency: 2200,
    cost: 34.70,
    trend: -2.5,
  },
];

const dateRanges = [
  { label: 'Last 24 Hours', value: '24h' },
  { label: 'Last 7 Days', value: '7d' },
  { label: 'Last 30 Days', value: '30d' },
  { label: 'Custom Range', value: 'custom' },
];

export default function Analytics() {
  const [selectedRange, setSelectedRange] = useState('7d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const textModels = mockModelData.filter(model => model.type === 'text');
  const imageModels = mockModelData.filter(model => model.type === 'image');
  const browserModels = mockModelData.filter(model => model.type === 'browser');

  const totalTokens = textModels.reduce((sum, model) => sum + (model.tokensUsed || 0), 0);
  const totalImages = imageModels.reduce((sum, model) => sum + (model.imagesGenerated || 0), 0);
  const totalBrowserActions = browserModels.reduce((sum, model) => sum + (model.browserActions || 0), 0);
  const totalCost = mockModelData.reduce((sum, model) => sum + model.cost, 0);
  const avgLatency = Math.round(mockModelData.reduce((sum, model) => sum + model.averageLatency, 0) / mockModelData.length);

  return (
    <div className="space-y-8">
      {/* Analytics Header */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DocumentChartBarIcon className="h-6 w-6 text-white/90" />
            <h1 className="text-xl font-medium text-white">Analytics</h1>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedRange}
              onChange={(e) => setSelectedRange(e.target.value)}
              className="input rounded"
            >
              {dateRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
            {selectedRange === 'custom' && (
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="input rounded"
                />
                <span className="text-white/50">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="input rounded"
                />
              </div>
            )}
            <button className="btn-secondary flex items-center space-x-2">
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="p-8">
          <div className="grid grid-cols-5 gap-6">
            <div className="glass-panel-dark p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/50">Total Tokens</div>
                <SparklesIcon className="h-5 w-5 text-white/30" />
              </div>
              <div className="mt-2">
                <div className="text-2xl font-semibold text-white">
                  {formatNumber(totalTokens)}
                </div>
                <div className="text-sm text-emerald-400">↑ 15.2% vs last week</div>
              </div>
            </div>
            <div className="glass-panel-dark p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/50">Images Generated</div>
                <PhotoIcon className="h-5 w-5 text-white/30" />
              </div>
              <div className="mt-2">
                <div className="text-2xl font-semibold text-white">
                  {formatNumber(totalImages)}
                </div>
                <div className="text-sm text-emerald-400">↑ 23.5% vs last week</div>
              </div>
            </div>
            <div className="glass-panel-dark p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/50">Browser Actions</div>
                <GlobeAltIcon className="h-5 w-5 text-white/30" />
              </div>
              <div className="mt-2">
                <div className="text-2xl font-semibold text-white">
                  {formatNumber(totalBrowserActions)}
                </div>
                <div className="text-sm text-emerald-400">↑ 12.8% vs last week</div>
              </div>
            </div>
            <div className="glass-panel-dark p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/50">Average Latency</div>
                <ClockIcon className="h-5 w-5 text-white/30" />
              </div>
              <div className="mt-2">
                <div className="text-2xl font-semibold text-white">
                  {avgLatency}ms
                </div>
                <div className="text-sm text-emerald-400">↓ 8.5% vs last week</div>
              </div>
            </div>
            <div className="glass-panel-dark p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/50">Total Cost</div>
                <CurrencyDollarIcon className="h-5 w-5 text-white/30" />
              </div>
              <div className="mt-2">
                <div className="text-2xl font-semibold text-white">
                  {formatCurrency(totalCost)}
                </div>
                <div className="text-sm text-rose-400">↑ 8.3% vs last week</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Trends */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">Usage Trends</h2>
        </div>
        <div className="p-8">
          <div className="h-80 flex items-center justify-center text-white/50">
            Chart will display token, image generation, and browser action trends over time
          </div>
        </div>
      </div>

      {/* Text Model Performance */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">Text Model Performance</h2>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {textModels.map((model) => (
            <div key={model.model} className="px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-white/[0.05] p-2 rounded">
                    <CpuChipIcon className="h-5 w-5 text-white/90" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">
                      {model.model}
                    </div>
                    <div className="text-sm text-white/50">
                      {formatNumber(model.tokensUsed || 0)} tokens • {model.averageLatency}ms avg latency
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-white">
                    {formatCurrency(model.cost)}
                  </div>
                  <div
                    className={`text-sm ${
                      model.trend > 0
                        ? 'text-rose-400'
                        : 'text-emerald-400'
                    }`}
                  >
                    {model.trend > 0 ? '↑' : '↓'} {Math.abs(model.trend)}% vs last month
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Browser Agent Performance */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">Browser Agent Performance</h2>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {browserModels.map((model) => (
            <div key={model.model} className="px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-white/[0.05] p-2 rounded">
                    <GlobeAltIcon className="h-5 w-5 text-white/90" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">
                      {model.model}
                    </div>
                    <div className="text-sm text-white/50">
                      {formatNumber(model.browserActions || 0)} actions • {model.averageLatency}ms avg latency
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-white">
                    {formatCurrency(model.cost)}
                  </div>
                  <div
                    className={`text-sm ${
                      model.trend > 0
                        ? 'text-rose-400'
                        : 'text-emerald-400'
                    }`}
                  >
                    {model.trend > 0 ? '↑' : '↓'} {Math.abs(model.trend)}% vs last month
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image Model Performance */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">Image Model Performance</h2>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {imageModels.map((model) => (
            <div key={model.model} className="px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-white/[0.05] p-2 rounded">
                    <PhotoIcon className="h-5 w-5 text-white/90" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">
                      {model.model}
                    </div>
                    <div className="text-sm text-white/50">
                      {formatNumber(model.imagesGenerated || 0)} images • {model.averageLatency}ms avg latency
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-white">
                    {formatCurrency(model.cost)}
                  </div>
                  <div
                    className={`text-sm ${
                      model.trend > 0
                        ? 'text-rose-400'
                        : 'text-emerald-400'
                    }`}
                  >
                    {model.trend > 0 ? '↑' : '↓'} {Math.abs(model.trend)}% vs last month
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Optimization Recommendations */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">
            Optimization Recommendations
          </h2>
        </div>
        <div className="p-8">
          <div className="space-y-4">
            <div className="glass-panel-dark p-6">
              <h3 className="text-white font-medium">Prompt Length Optimization</h3>
              <p className="mt-1 text-sm text-white/50">
                Reduce costs by optimizing prompt lengths. Current average: 250 tokens.
                Target: {'<'}200 tokens.
              </p>
              <button className="mt-4 btn-secondary text-sm">View Details</button>
            </div>
            <div className="glass-panel-dark p-6">
              <h3 className="text-white font-medium">Browser Action Efficiency</h3>
              <p className="mt-1 text-sm text-white/50">
                Optimize browser agent workflows to reduce redundant actions and improve performance.
              </p>
              <button className="mt-4 btn-secondary text-sm">Learn More</button>
            </div>
            <div className="glass-panel-dark p-6">
              <h3 className="text-white font-medium">Image Resolution Strategy</h3>
              <p className="mt-1 text-sm text-white/50">
                Save 30% by using lower resolutions for thumbnail generation.
              </p>
              <button className="mt-4 btn-secondary text-sm">Learn More</button>
            </div>
            <div className="glass-panel-dark p-6">
              <h3 className="text-white font-medium">Response Caching</h3>
              <p className="mt-1 text-sm text-white/50">
                Implement caching for frequently requested completions and images.
              </p>
              <button className="mt-4 btn-secondary text-sm">Configure Cache</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
